import * as React from 'react';

import type { JsonObject } from '@/app-package';
import { AppointmentDoc } from '@bb/domain/dist/schemas';
import PanelHeader from '../../react-common/PanelHeader';
import PanelBody from '../../react-common/PanelBody';
import Loading from '../../react-common/Loading';
import Pagination from '../../react-common/Pagination';
import { getAppointmentsForCoop } from './APIservice';
import PreviousAppointmentDetailsGroup from './PreviousAppointmentDetailsGroup';
import UpcomingAppointmentDetailsGroup from './UpcomingAppointmentDetailsGroup';
import sortAppointments from './sortAppointments';

type AppointmentsPageProps = { $routeParams: any };

type AppointmentsPageState = {
  activeTab: 'upcoming' | 'previous';
  appointments: JsonObject<AppointmentDoc>[];
  appointmentsCount: number;
  page: number;
  isDataLoading: boolean;
};

const AppointmentsPageHeader = () => (
  <>
    <a href="/#/my_business" className="listing-link">
      My Business
    </a>{' '}
    &gt; Showings
  </>
);

class AppointmentsPage extends React.Component<
  AppointmentsPageProps,
  AppointmentsPageState
> {
  constructor(props: AppointmentsPageProps) {
    super(props);

    const { $routeParams } = this.props;

    this.state = {
      activeTab: $routeParams.initialTab || 'previous',
      appointments: [],
      appointmentsCount: 0,
      page: 1,
      isDataLoading: false,
    };
  }

  async componentDidMount() {
    const { activeTab } = this.state;

    this.setState({ isDataLoading: true });
    const { appointments, count } = await getAppointmentsForCoop(
      1,
      activeTab === 'previous' ? 'past' : undefined,
    );
    this.setState({
      isDataLoading: false,
      appointments,
      appointmentsCount: count,
    });
  }

  onUpcomingButtonClick = async () => {
    const { activeTab } = this.state;
    if (activeTab === 'upcoming') return;
    this.setState({ isDataLoading: true });
    const { appointments, count } = await getAppointmentsForCoop(1);
    this.setState({
      isDataLoading: false,
      activeTab: 'upcoming',
      appointments,
      appointmentsCount: count,
      page: 1,
    });
  };

  onPreviousButtonClick = async () => {
    const { activeTab } = this.state;
    if (activeTab === 'previous') return;
    this.setState({ isDataLoading: true });
    const { appointments, count } = await getAppointmentsForCoop(1, 'past');
    this.setState({
      isDataLoading: false,
      activeTab: 'previous',
      appointments,
      appointmentsCount: count,
      page: 1,
    });
  };

  onPageChange = async (page: number) => {
    const { activeTab } = this.state;
    this.setState({ page, isDataLoading: true });

    let result;
    if (activeTab === 'upcoming') {
      result = await getAppointmentsForCoop(page);
    } else {
      result = await getAppointmentsForCoop(page, 'past');
    }

    const { appointments, count } = result;
    this.setState({
      isDataLoading: false,
      appointments,
      appointmentsCount: count,
    });
  };

  render() {
    const { activeTab, appointments, appointmentsCount, isDataLoading, page } =
      this.state;
    const { sortedDates, groupedAppointmentsByDate } = sortAppointments(
      appointments,
      activeTab === 'upcoming' ? 'asc' : 'desc',
    );

    const activeButtonClassName = 'btn btn-info';
    const inactiveButtonClassName = 'btn btn-default';

    const AppointmentDetailsGroup =
      activeTab === 'upcoming'
        ? UpcomingAppointmentDetailsGroup
        : PreviousAppointmentDetailsGroup;

    return (
      <>
        <PanelHeader title={<AppointmentsPageHeader />} />
        <PanelBody>
          <div
            className="container showing-appointments"
            style={{ width: '100%' }}
          >
            <div className="row">
              <div className="col-xs-4">
                <div className="appointment-navbar">
                  <button
                    className={
                      activeTab === 'upcoming'
                        ? activeButtonClassName
                        : inactiveButtonClassName
                    }
                    onClick={this.onUpcomingButtonClick}
                    data-cy="go-upcoming-showings"
                  >
                    Upcoming
                  </button>
                  <button
                    className={
                      activeTab === 'previous'
                        ? activeButtonClassName
                        : inactiveButtonClassName
                    }
                    onClick={this.onPreviousButtonClick}
                  >
                    Previous
                  </button>
                </div>
              </div>
              {appointmentsCount > 20 && (
                <div className="pull-right">
                  <Pagination
                    itemsPerPage={20}
                    totalItems={appointmentsCount}
                    currentPage={page}
                    onChange={this.onPageChange}
                  />
                </div>
              )}
            </div>
            {isDataLoading ? (
              <Loading size="fa-5x" />
            ) : (
              <div className="row">
                <div className="col-lg-9">
                  {sortedDates.map(date => (
                    <React.Fragment key={date}>
                      <hr />
                      <AppointmentDetailsGroup
                        key={date}
                        appointmentDate={date}
                        appointments={groupedAppointmentsByDate[date]}
                      />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </PanelBody>
      </>
    );
  }
}

export default AppointmentsPage;
